import { theme } from "@polifonia/theme";

// Type for valid hex color strings
type HexColor = `#${string}` & {
  __brand: "HexColor"; // This is a type branding technique to make the type more strict
};

// Type guard to validate hex color strings
function isValidHexColor(color: string): color is HexColor {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/.test(color);
}

/**
 * 📐 Calculation Formula:
 * Luminance = 0.2126 × R + 0.7152 × G + 0.0722 × B
 *
 * R/G/B are the RGB values converted to a 0-1 range and with brightness correction applied.
 * Green has the highest weight because the human eye is most sensitive to it.
 *
 * ✅ How to use it in practice?
 * We consider luminance less than 0.6 as "dark color" → use white text
 * We consider luminance greater than or equal to 0.6 as "light color" → use dark blue-gray text
 */
function getLuminance(hex: string) {
  if (!isValidHexColor(hex)) {
    throw new Error("Invalid hex color");
  }
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const toLinear = (c: number) => {
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };

  const R = toLinear(r);
  const G = toLinear(g);
  const B = toLinear(b);

  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

function getTextColorFromBackgroundColor(backgroundColor: string) {
  return getLuminance(backgroundColor) < 0.6
    ? theme.colors.white
    : theme.colors.neutral090;
}

export { getLuminance, getTextColorFromBackgroundColor, isValidHexColor };
